// Per-district + national turnout history, multi-year.
//
// Sources (every value either traceable to a primary Electoral
// Commission notification or to district_summaries_*.csv in the
// project's handover folder):
//
//   2003 — per-district 2pm + 11pm + winner, commission table.
//   2008 — per-district 2pm + 11pm + winner, commission table.
//   2013 — per-district 2pm + 11pm (no winner column on the source
//          table — 2013 was a landslide PL national win).
//   2017 — per-district 2pm from the commission notification of
//          3 June 2017 ("Approximate percentage voting turnout up
//          to 2.00 pm"); national closing 92.1% (commission final
//          publication, matches the figure widely reported by
//          MaltaToday / Times of Malta / handover archive).
//   2022 — per-district 2pm, commission notification of 26 March
//          2022 ("Approximate percentage voting turnout up to
//          2.00 pm"); closing turnout + winner + margin still live
//          in src/data/malta_districts_2022.geojson alongside the
//          full vote-share figures.
//
// Closing-turnout history for the /turnout deep-dive table also
// lives in src/data/district_turnout_history.json. That JSON is
// the canonical source for THAT surface; this TS module is the
// canonical source for runtime comparisons (cube grid + hero
// delta) because it's typed + bundle-loaded + carries per-year
// 2pm figures the JSON doesn't have.
//
// Integer percentages from the 2003/2008/2013 commission tables
// are stored as integers (no false precision); the 2017/2022 2pm
// figures and 2022 closing are decimals because the original
// notifications were decimals.

export type Wave = 'pct_2pm' | 'pct_close';
export type ElectionYear = 2003 | 2008 | 2013 | 2017 | 2022;
export type Party = 'PL' | 'PN';

/** One district's reading for one election year. */
export interface HistoricalDistrictReading {
  /** Mid-day turnout %. The Electoral Commission's 2pm
   *  ("approximate percentage voting turnout up to 2.00 pm")
   *  notification. */
  pct_2pm: number;
  /** Closing turnout % — typically the 11pm reading historically,
   *  22:00 since polls now close earlier. Missing for years where
   *  we only have the 2pm figure. */
  pct_close: number | null;
  /** Which party carried the district. Recorded where the source
   *  table includes it (2003, 2008, 2022). Missing for 2013/2017
   *  because the source tables didn't include the winner column. */
  winner: Party | null;
}

export type YearHistory = Record<number, HistoricalDistrictReading>;

// ── 2003 ────────────────────────────────────────────────────────
const HISTORY_2003: YearHistory = {
  1:  { pct_2pm: 56, pct_close: 96, winner: 'PN' },
  2:  { pct_2pm: 49, pct_close: 96, winner: 'PL' },
  3:  { pct_2pm: 48, pct_close: 96, winner: 'PL' },
  4:  { pct_2pm: 52, pct_close: 96, winner: 'PL' },
  5:  { pct_2pm: 47, pct_close: 96, winner: 'PL' },
  6:  { pct_2pm: 42, pct_close: 96, winner: 'PL' },
  7:  { pct_2pm: 47, pct_close: 97, winner: 'PN' },
  8:  { pct_2pm: 51, pct_close: 96, winner: 'PN' },
  9:  { pct_2pm: 53, pct_close: 95, winner: 'PN' },
  10: { pct_2pm: 57, pct_close: 95, winner: 'PN' },
  11: { pct_2pm: 48, pct_close: 96, winner: 'PN' },
  12: { pct_2pm: 47, pct_close: 95, winner: 'PN' },
  13: { pct_2pm: 47, pct_close: 95, winner: 'PN' },
};

// ── 2008 ────────────────────────────────────────────────────────
const HISTORY_2008: YearHistory = {
  1:  { pct_2pm: 46, pct_close: 93, winner: 'PL' },
  2:  { pct_2pm: 45, pct_close: 94, winner: 'PL' },
  3:  { pct_2pm: 41, pct_close: 94, winner: 'PL' },
  4:  { pct_2pm: 51, pct_close: 94, winner: 'PL' },
  5:  { pct_2pm: 43, pct_close: 94, winner: 'PL' },
  6:  { pct_2pm: 46, pct_close: 94, winner: 'PL' },
  7:  { pct_2pm: 40, pct_close: 94, winner: 'PL' },
  8:  { pct_2pm: 48, pct_close: 94, winner: 'PN' },
  9:  { pct_2pm: 48, pct_close: 93, winner: 'PN' },
  10: { pct_2pm: 47, pct_close: 92, winner: 'PN' },
  11: { pct_2pm: 46, pct_close: 94, winner: 'PN' },
  12: { pct_2pm: 38, pct_close: 91, winner: 'PN' },
  13: { pct_2pm: 44, pct_close: 92, winner: 'PN' },
};

// ── 2013 (no winner column in source table) ─────────────────────
const HISTORY_2013: YearHistory = {
  1:  { pct_2pm: 50, pct_close: 93, winner: null },
  2:  { pct_2pm: 45, pct_close: 95, winner: null },
  3:  { pct_2pm: 44, pct_close: 94, winner: null },
  4:  { pct_2pm: 55, pct_close: 95, winner: null },
  5:  { pct_2pm: 53, pct_close: 94, winner: null },
  6:  { pct_2pm: 53, pct_close: 94, winner: null },
  7:  { pct_2pm: 49, pct_close: 94, winner: null },
  8:  { pct_2pm: 49, pct_close: 94, winner: null },
  9:  { pct_2pm: 46, pct_close: 92, winner: null },
  10: { pct_2pm: 51, pct_close: 90, winner: null },
  11: { pct_2pm: 51, pct_close: 93, winner: null },
  12: { pct_2pm: 47, pct_close: 90, winner: null },
  13: { pct_2pm: 50, pct_close: 92, winner: null },
};

// ── 2017 (commission only released 2pm — closing in JSON) ───────
const HISTORY_2017: YearHistory = {
  1:  { pct_2pm: 56.24, pct_close: null, winner: null },
  2:  { pct_2pm: 52.24, pct_close: null, winner: null },
  3:  { pct_2pm: 49.51, pct_close: null, winner: null },
  4:  { pct_2pm: 55.42, pct_close: null, winner: null },
  5:  { pct_2pm: 49.74, pct_close: null, winner: null },
  6:  { pct_2pm: 52.18, pct_close: null, winner: null },
  7:  { pct_2pm: 49.26, pct_close: null, winner: null },
  8:  { pct_2pm: 54.11, pct_close: null, winner: null },
  9:  { pct_2pm: 53.11, pct_close: null, winner: null },
  10: { pct_2pm: 51.69, pct_close: null, winner: null },
  11: { pct_2pm: 50.98, pct_close: null, winner: null },
  12: { pct_2pm: 49.32, pct_close: null, winner: null },
  13: { pct_2pm: 49.61, pct_close: null, winner: null },
};

// ── 2022 (closing + winner via geojson — kept here for the 2pm) ──
const HISTORY_2022: YearHistory = {
  1:  { pct_2pm: 43.85, pct_close: null, winner: null },
  2:  { pct_2pm: 38.26, pct_close: null, winner: null },
  3:  { pct_2pm: 38.87, pct_close: null, winner: null },
  4:  { pct_2pm: 42.81, pct_close: null, winner: null },
  5:  { pct_2pm: 40.23, pct_close: null, winner: null },
  6:  { pct_2pm: 39.32, pct_close: null, winner: null },
  7:  { pct_2pm: 39.05, pct_close: null, winner: null },
  8:  { pct_2pm: 42.55, pct_close: null, winner: null },
  9:  { pct_2pm: 42.47, pct_close: null, winner: null },
  10: { pct_2pm: 41.47, pct_close: null, winner: null },
  11: { pct_2pm: 39.28, pct_close: null, winner: null },
  12: { pct_2pm: 37.72, pct_close: null, winner: null },
  13: { pct_2pm: 40.50, pct_close: null, winner: null },
};

/** Full per-year, per-district history. */
export const TURNOUT_HISTORY: Readonly<Record<ElectionYear, YearHistory>> = {
  2003: HISTORY_2003,
  2008: HISTORY_2008,
  2013: HISTORY_2013,
  2017: HISTORY_2017,
  2022: HISTORY_2022,
};

/** National-level summary per year. From the "TOTAL" / "Average"
 *  rows of the same commission tables. 2017 national 2pm is from
 *  the commission notification text (52.32%); 2017 national close
 *  is the commission's final-publication figure (92.1%) — also
 *  the value carried by the legacy ElectionDay/TurnoutHistoryChart
 *  so the two surfaces are consistent. */
export const NATIONAL_TURNOUT_HISTORY: Readonly<
  Record<ElectionYear, { pct_2pm: number; pct_close: number | null }>
> = {
  2003: { pct_2pm: 49,    pct_close: 96   },
  2008: { pct_2pm: 45,    pct_close: 93.3 },
  2013: { pct_2pm: 49,    pct_close: 93   },
  2017: { pct_2pm: 52.32, pct_close: 92.1 },
  2022: { pct_2pm: 40.45, pct_close: 85.6 },
};

// ── Back-compat aliases (consumed by Hero + TurnoutCubeGrid) ────
// Old export names point at the new structure so existing callers
// keep working without import churn. Migrate to TURNOUT_HISTORY +
// NATIONAL_TURNOUT_HISTORY directly when those surfaces are
// rebuilt.

export interface DistrictTurnoutBaseline2022 {
  pct_2pm: number;
}

export const TURNOUT_2022_2PM: Readonly<Record<number, DistrictTurnoutBaseline2022>> =
  Object.fromEntries(
    Object.entries(HISTORY_2022).map(([k, v]) => [Number(k), { pct_2pm: v.pct_2pm }]),
  );

export const TURNOUT_2022_2PM_NATIONAL = NATIONAL_TURNOUT_HISTORY[2022].pct_2pm;
export const TURNOUT_2022_CLOSE_NATIONAL =
  NATIONAL_TURNOUT_HISTORY[2022].pct_close ?? 85.6;
